import store from '@/store/store';
import { formatDateString } from './FormatService';
import { CmsField, DigitalAcceptCmsPage, Payment, Policy } from '@/types/types';
import { INPUT_TYPES, POLICY_FIELDS } from '@/types/enums';

const ignoreFieldsInUpdateInsuranceObject = new Set([
	POLICY_FIELDS.payment_frequency.valueOf(),
	POLICY_FIELDS.payment_method.valueOf(),
	POLICY_FIELDS.cover_start_date.valueOf(),
	POLICY_FIELDS.account.valueOf(),
	POLICY_FIELDS.reg_no.valueOf(),
	POLICY_FIELDS.previous_company_id.valueOf(),
	POLICY_FIELDS.previous_policy_id.valueOf(),
	POLICY_FIELDS.termination_option.valueOf(),
]);

export const createDigitalAcceptSubmitRequest = () => {
	const policies = createPolicies();

	const digitalAcceptResponse: DigitalAcceptCmsPage = {
		policies,
	};
	return digitalAcceptResponse;
};

const createPolicies = (): Array<Policy> => {
	const policies: Array<Policy> = [];

	const policyIds = store.state.ProposalContext.originalRequest.policies.map((policy) => policy.id);
	policyIds.forEach((policyId) => {
		const policy = store.state.ProposalContext.proposalSteps.get(policyId);

		const fields = policy.inputFields;

		const termination =
			getFieldValue(fields, POLICY_FIELDS.termination_option) === 'CHANGE_OF_OWNERSHIP'
				? {
						previous_company_id: getFieldValue(fields, POLICY_FIELDS.previous_company_id),
						previous_policy_id: getFieldValue(fields, POLICY_FIELDS.previous_policy_id),
						termination_option: getFieldValue(fields, POLICY_FIELDS.termination_option),
					}
				: null;

		const paymentMethod = getFieldValue(fields, POLICY_FIELDS.payment_method) as 'FI_PRÆM' | 'PBS';

		// payment Null if FI_præm eller PBS på on load
		const payment: Payment = {
			payment_method: paymentMethod,
			payment_frequency: Number.parseInt(getFieldValue(fields, POLICY_FIELDS.payment_frequency)) as 1 | 12,
			payment_account: null,
		};

		if (paymentMethod === 'PBS' && getFieldValue(fields, POLICY_FIELDS.reg_no))
			payment.payment_account = {
				reg_no: Number.parseInt(getFieldValue(fields, POLICY_FIELDS.reg_no)),
				account: Number.parseInt(getFieldValue(fields, POLICY_FIELDS.account)),
			};

		const policyResponse: Policy = {
			id: policyId,
			cover_start_date: getFieldValueDate(fields, POLICY_FIELDS.cover_start_date),
			update_insurance_object: getUpdateInsuranceObject(fields),
			termination,
			...payment,
		};
		policies.push(policyResponse);
	});
	return policies;
};

const getUpdateInsuranceObject = (inputFields: Array<CmsField>) => {
	const fieldsToUpdate = getSubmitToApiInsuranceFields(inputFields);
	if (!fieldsToUpdate) {
		// if there hasn't been any changes to the insurance - skip updating the insurance object
		return null;
	}
	// POLICY_FIELDS.insurance_type is hidden and readonly, but required to send to API if there is updates to an insurance.
	const insuranceType = inputFields.find((field) => field.id === POLICY_FIELDS.insurance_type)?.value as string;
	return {
		insurance_type: insuranceType,
		...fieldsToUpdate,
	};
};

const getFieldValue = (fields: Array<any>, fieldId: string, ignoreSubmitToApi: boolean = false) => {
	return fields.find((field) => (ignoreSubmitToApi || field.submitToApi) && field.id === fieldId)?.value;
};

const getFieldValueDate = (fields: Array<any>, fieldId: string) => {
	return formatDateString(fields.find((field) => field.submitToApi && field.id === fieldId)?.value, false);
};

/**
 * If any field is updated, we have to send (nearly!) all the policy_object fields to the API.
 * @param inputFields
 * @returns all inputFields as an object for update or null
 */
const getSubmitToApiInsuranceFields = (inputFields: Array<CmsField>) => {
	let hasUpdates = false;
	const result = {};
	const insuranceType = inputFields.find((field) => field.id === POLICY_FIELDS.insurance_type)?.value as string;

	inputFields.forEach((field) => {
		if (
			field.submitToApi &&
			!ignoreFieldsInUpdateInsuranceObject.has(field.id) &&
			isFieldDependencyValidOnInsuranceType(field, insuranceType)
		) {
			// compare with formatted value to avoid sending updates if the value only has been reformatted in the accept flow, ex datepicker or number field
			const formattedValue = getFieldTypeValueFormatted(field);
			if (
				field.readonly ||
				field.initialValue === formattedValue ||
				'' + field.initialValue === '' + formattedValue
			) {
				// add to result, but indicate nothing has changed.
				result[field.id] = formattedValue;
				return;
			}
			if (formattedValue || formattedValue === false) {
				// add to result, and set update.
				result[field.id] = formattedValue;
				hasUpdates = true;
			}
		}
	});
	return hasUpdates ? result : null;
};

const isFieldDependencyValidOnInsuranceType = (field: CmsField, insuranceType: string) => {
	if (!field.dependentId || !insuranceType) {
		return true;
	}
	if (field.dependentId === POLICY_FIELDS.insurance_type && field.dependentValue === insuranceType) {
		return true;
	}

	return false;
};

const getFieldTypeValueFormatted = (field: CmsField) => {
	if (!field.value && field.value !== false) {
		return;
	}
	switch (field.contentType) {
		case INPUT_TYPES.TextField:
			return field.type === 'number' ? Number.parseInt(field.value as string) : field.value;
		case INPUT_TYPES.DatePickerField:
			return formatDateString(field.value as string, false);
		case INPUT_TYPES.CheckField:
			if (field.value === 'true' || field.value === 'false') {
				return JSON.parse(field.value);
			}
			return field.value;

		default:
			return field.value;
	}
};
